import React, { useEffect, useState } from 'react';

import css from './EventDossier.module.css';
import useNavigation from '../../../hooks/useNavigation';
import LoadingState from '../RegulationDossier/LoadingState/LoadingState';
import useEvent from '../../../hooks/useEvent';
import DossierHeader from '../../../../components/DossierHeader/DossierHeader';
import DossierAvatar from '../DossierAvatar/DossierAvatar';
import EntityPill from '../../../../components/EntityPill/EntityPill';
import BriefPill from '../../../../components/BriefPill/BriefPill';
import Event from '../../../../icons/Event';
import Source from '../../Source/Source';
import { formatDateMed } from '../../../../lib/date';

function EventDossier({ eventId, size, onSizeChange }) {
  const { loading, event } = useEvent(eventId);
  const { params, navigate, location } = useNavigation();

  const onPillClicked = (entity) => {
    params.delete('dossierEventId');
    params.set('dossierEntityId', entity.id);
    params.set('dossierType', 'entity');
    navigate(location.pathname, params, { back: `Back to Event` });
  };

  if (loading) return <LoadingState />;
  if (!event) return null;

  const hasTags = event.briefs.length > 0 || event.countries.length > 0;

  return (
    <div className={css.main}>
      <div className={css.header}>
        <DossierHeader size={size} onSizeChange={onSizeChange} />
        <div
          style={{ marginTop: -5, zIndex: 5, position: 'relative' }}
          className={css.divider}
        />
      </div>
      <div className={css.content}>
        <DossierAvatar>
          <div className={css.icon}>
            <Event />
          </div>
        </DossierAvatar>

        <h3 className={css.name}>{event.title}</h3>

        <div className={css.item}>
          <div className={css.itemTitle}>Date</div>
          <div className={css.itemContent}>{formatDateMed(event.date)}</div>
        </div>

        <div className={css.item}>
          <div className={css.itemTitle}>Entities</div>
          <div className={`${css.itemContent} ${css.pill}`}>
            <div className={css.list}>
              {event.entities?.map((entity) => (
                <EntityPill
                  key={entity.id}
                  image={entity.image}
                  name={entity.name}
                  shortName={entity.shortestName}
                  type={entity.type}
                  className={css.pill}
                  isConnected={entity.isConnected}
                  onClick={() => onPillClicked(entity)}
                  size="small"
                />
              ))}
            </div>
          </div>
        </div>

        {hasTags && (
          <div className={css.item}>
            <div className={css.itemTitle}>Tags</div>
            <div className={css.itemContent}>
              <div className={css.list}>
                {event.briefs?.map((brief) => (
                  <BriefPill key={brief.id} name={brief.name} />
                ))}

                {event.countries?.map((country) => (
                  <BriefPill key={country.id} name={country.name} isGeography />
                ))}
              </div>
            </div>
          </div>
        )}

        <div className={css.item}>
          <div className={css.itemTitle}>Source</div>
          <div className={css.itemContent}>
            <div className={css.list}>
              {event.sources?.map((source) => (
                <Source src={source} key={source.key} />
              ))}
            </div>
          </div>
        </div>
      </div>
      <div className={css.divider} />

      {event.summary && (
        <div className={css.summaryCard}>
          <div className={css.summaryCardTitle}>Summary</div>
          <div className={css.summaryCardContent}>{event.summary}</div>
          <div className={css.summaryCardContent}>
            <ul>
              {event.bullets?.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}

export default function EventDossierWrapper({ size, onSizeChange }) {
  const [isDossierOpen, setIsDossierOpen] = useState(false);
  const { params } = useNavigation();

  useEffect(() => {
    const dossierEventId = params.get('dossierEventId');
    const dossierType = params.get('dossierType');

    if (dossierEventId && dossierType) {
      setIsDossierOpen(true);
    } else {
      setIsDossierOpen(false);
    }
  }, [params]);

  if (!isDossierOpen) return null;

  return (
    <EventDossier
      size={size}
      eventId={params.get('dossierEventId')}
      onSizeChange={onSizeChange}
    />
  );
}
